import { DOCUMENT } from '@angular/common';
import { ErrorHandler, inject, Injectable, Injector } from '@angular/core';
import { LoggerService } from '@rms/core/logger/logger.service';
import { NotificationService } from '@rms/core/notification';
import { AppError } from '@rms/sdk/errors';

import { isChunkLoadError, isImageLoadError, promptReloadForFreshChunks } from './chunk-load-recovery';

const DUPLICATE_NOTIFICATION_WINDOW_MS = 4000;

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly injector = inject(Injector);
  private readonly document = inject(DOCUMENT);

  private lastNotifiedKey: string | null = null;
  private lastNotifiedAt = 0;

  public handleError(error: unknown): void {
    const original = this.unwrap(error);
    const logger = this.injector.get(LoggerService);

    // Safety net for dynamic-import failures that reach the global handler (lazy
    // route loads are handled earlier by the router navigation-error handler).
    // Prompt the user to reload for fresh bundles instead of reloading silently.
    if (isChunkLoadError(original)) {
      logger.warn('Lazy chunk failed to load; prompting user to reload', 'GlobalErrorHandler.chunkReload');
      promptReloadForFreshChunks(this.injector.get(NotificationService), this.document.defaultView);

      return;
    }

    if (isImageLoadError(original)) {
      return;
    }

    if (original instanceof AppError) {
      logger.error(original.source, `Unhandled AppError: ${original.name}`);
      this.notify(original.translationKey ? original.translationKey : original.message);

      return;
    }

    logger.error(original, 'Unhandled error');
    this.notify('common.errors.unexpected');
  }

  private notify(translationKey: string): void {
    const now = Date.now();

    if (translationKey === this.lastNotifiedKey && now - this.lastNotifiedAt < DUPLICATE_NOTIFICATION_WINDOW_MS) {
      return;
    }

    this.lastNotifiedKey = translationKey;
    this.lastNotifiedAt = now;
    this.injector.get(NotificationService).error(translationKey);
  }

  // Angular wraps zone and unhandled-rejection errors; reach the original before the instanceof check.
  private unwrap(error: unknown): unknown {
    if (typeof error === 'object' && error !== null) {
      if ('rejection' in error) {
        return error.rejection;
      }

      if ('ngOriginalError' in error) {
        return error.ngOriginalError;
      }
    }

    return error;
  }
}
